import { lazy } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  getDashboardStats,
  getDestinations,
  getExports,
  getPipelines,
  getSources,
  getUsers,
} from '../services/api';

// DATA-340 (PF-08): each page's code downloads only when it's first opened,
// instead of all eight pages up front. Hovering a nav link starts both the
// code download and the page's main data request, so the page usually opens
// already filled in.

const loaders = {
  '/dashboard': () => import('./Dashboard'),
  '/sources': () => import('./Sources'),
  '/destinations': () => import('./Destinations'),
  '/pipelines': () => import('./Pipelines'),
  '/runs': () => import('./Runs'),
  '/notifications': () => import('./Notifications'),
  '/exports': () => import('./Exports'),
  '/settings': () => import('./Settings'),
};

export const Dashboard = lazy(loaders['/dashboard']);
export const Sources = lazy(loaders['/sources']);
export const Destinations = lazy(loaders['/destinations']);
export const Pipelines = lazy(loaders['/pipelines']);
export const Runs = lazy(loaders['/runs']);
export const Notifications = lazy(loaders['/notifications']);
export const Exports = lazy(loaders['/exports']);
export const Settings = lazy(loaders['/settings']);

// Each page's main queries. Keys and functions must match the page's own
// useQuery exactly, or the prefetch fills a cache entry nobody reads.
const sources = { queryKey: ['sources'], queryFn: () => getSources().then((r) => r.data) };
const destinations = { queryKey: ['destinations'], queryFn: () => getDestinations().then((r) => r.data) };
const pipelines = { queryKey: ['pipelines'], queryFn: () => getPipelines().then((r) => r.data) };

const pageQueries: Record<string, { queryKey: unknown[]; queryFn: () => Promise<unknown> }[]> = {
  '/dashboard': [{ queryKey: ['dashboard'], queryFn: () => getDashboardStats().then((r) => r.data) }],
  '/sources': [sources],
  '/destinations': [destinations],
  '/pipelines': [pipelines, sources, destinations],
  '/runs': [pipelines],
  '/exports': [{ queryKey: ['exports'], queryFn: () => getExports().then((r) => r.data) }, sources],
  '/settings': [{ queryKey: ['users'], queryFn: () => getUsers().then((r) => r.data) }],
};

/** Warm a page before it's opened. Safe to call repeatedly: the code import
 *  is cached by the bundler, and prefetchQuery skips data that's still fresh. */
export function prefetchPage(path: string, queryClient: QueryClient): void {
  const load = loaders[path as keyof typeof loaders];
  if (load) load().catch(() => undefined);   // a failed prefetch just means no head start
  for (const q of pageQueries[path] ?? []) {
    queryClient.prefetchQuery({ ...q, staleTime: 30_000 }).catch(() => undefined);
  }
}
